import { useEffect, useState } from "react";
import { CalendarClock, Pencil, Plus, X } from "lucide-react";
import dayjs from "dayjs";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../common/ToastMsg";
import { DEMO_PREFERENCE_URL } from "../../../constants/api";

const FIELD = "min-w-0 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-800 dark:text-gray-100";

/**
 * Demo slots the lead picked on the website's confirm-details page
 * (email_integration/lead_profile.py), earliest first; past slots stay listed
 * but greyed so the rep sees what was asked for even after the date. Staff can
 * edit them too (PUT, written to the timeline as a note); the demo reminder
 * page offers these times first when the lead moves their demo.
 */
export default function DemoSlotsCard({ customerId }) {
    const { fetch, update } = useApiMethods();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        if (!customerId) return undefined;
        fetch(DEMO_PREFERENCE_URL(customerId)).then(res => {
            if (live && res?.status === "success") setData(res.data);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    if (!data) return null;
    const timeSlots = data.time_slots || [];
    const max = data.max_slots || 3;

    const startEdit = () => {
        const rows = (data.slots || []).filter(s => !s.past).map(s => ({ date: s.date, time: s.time }));
        setDraft(rows.length ? rows : [{ date: "", time: timeSlots[0] || "" }]);
    };
    const setRow = (i, patch) => setDraft(d => d.map((r, j) => (j === i ? { ...r, ...patch } : r)));

    const save = async () => {
        const slots = draft.filter(r => r.date && r.time);
        setSaving(true);
        const res = await update(DEMO_PREFERENCE_URL(customerId), { slots });
        setSaving(false);
        if (res?.status === "success") {
            setData(res.data);
            setDraft(null);
            ToastMsg("success", "Preferred demo slots saved");
        } else {
            ToastMsg("error", res?.message || "Could not save the slots");
        }
    };

    const slots = data.slots || [];

    return (
        <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/20 p-4">
            <div className="flex items-center gap-1.5 mb-2">
                <CalendarClock className="h-3.5 w-3.5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                <p className="text-[10px] font-bold text-teal-700 dark:text-teal-300 uppercase tracking-widest flex-1">
                    Preferred demo slots
                </p>
                {!draft && slots.length > 0 && (
                    <button type="button" onClick={startEdit} aria-label="Edit preferred slots"
                            className="text-teal-700 dark:text-teal-300 hover:text-teal-900">
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>

            {draft ? (
                <div className="space-y-2">
                    {draft.map((r, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            <input type="date" aria-label={`Slot ${i + 1} date`} className={`${FIELD} w-32`}
                                   min={dayjs().format("YYYY-MM-DD")} value={r.date}
                                   onChange={e => setRow(i, { date: e.target.value })} />
                            <select aria-label={`Slot ${i + 1} time`} className={`${FIELD} flex-1`} value={r.time}
                                    onChange={e => setRow(i, { time: e.target.value })}>
                                {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <button type="button" aria-label={`Remove slot ${i + 1}`}
                                    onClick={() => setDraft(d => d.filter((_, j) => j !== i))}
                                    className="text-gray-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                        </div>
                    ))}
                    {draft.length < max && (
                        <button type="button" onClick={() => setDraft(d => [...d, { date: "", time: timeSlots[0] || "" }])}
                                className="flex items-center gap-1 text-[11px] font-medium text-teal-700 dark:text-teal-300 hover:underline">
                            <Plus className="h-3 w-3" /> Add a slot
                        </button>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={() => setDraft(null)}
                                className="px-2.5 py-1 rounded-md text-xs text-gray-600 dark:text-gray-300 hover:bg-white/60">Cancel</button>
                        <button type="button" onClick={save} disabled={saving}
                                className="px-2.5 py-1 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 disabled:opacity-60">
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Up to {max} slots. The change is noted on the timeline.</p>
                </div>
            ) : slots.length ? (
                <ul className="space-y-1.5">
                    {slots.map(s => (
                        <li key={`${s.date}-${s.time}`}
                            className={s.past ? "text-[11px] text-gray-400 line-through" : "text-xs font-medium text-gray-800 dark:text-gray-100"}>
                            {dayjs(s.date).format("ddd, DD MMM YYYY")}
                            <span className={s.past ? "" : "text-gray-500 dark:text-gray-400 font-normal"}> · {s.time}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <button type="button" onClick={startEdit}
                        className="flex items-center gap-1 text-xs font-medium text-teal-700 dark:text-teal-300 hover:underline">
                    <Plus className="h-3 w-3" /> Add preferred slots
                </button>
            )}
            {!draft && data.updated_at && (
                <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400">
                    Last changed {dayjs(data.updated_at).format("DD MMM YYYY")}
                </p>
            )}
        </div>
    );
}
